import { Box, Button, MenuItem, TextField, CircularProgress } from '@mui/material';
import { Formik } from 'formik';
import React, { FC } from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import BankingService from 'services/banking.service';
import { EPixOperationType, ETransactionAccountType } from 'types/banking';
import { formatCurrency, parseCurrency, maskCpfCnpj } from 'utils/banking';
import BrazilianBankSearch from '../../../components/BrazilianBankSearch';

enum EAccountTypePortuguese {
  CORRENTE = 'CORRENTE',
  POUPANCA = 'POUPANCA',
  PAGAMENTO = 'PAGAMENTO',
}

const ACCOUNT_TYPE_MAP: Record<EAccountTypePortuguese, ETransactionAccountType> = {
  [EAccountTypePortuguese.CORRENTE]: ETransactionAccountType.CHECKING,
  [EAccountTypePortuguese.POUPANCA]: ETransactionAccountType.SAVINGS,
  [EAccountTypePortuguese.PAGAMENTO]: ETransactionAccountType.PAYMENT,
};

type FlowProps = {
  onSuccess: () => void;
  onCancel: () => void;
  onSubmitting: (loading: boolean) => void;
};

const PixByBankData: FC<FlowProps> = ({ onSuccess, onCancel, onSubmitting }) => {
  const bankingService = new BankingService();
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Formik
      initialValues={{
        document: '',
        bankIspb: '',
        agency: '',
        account: '',
        accountType: '',
        amount: '',
        date: new Date().toLocaleDateString('en-CA'),
        description: '',
      }}
      validationSchema={Yup.object({
        document: Yup.string().required('Documento obrigatório'),
        bankIspb: Yup.string().required('Instituição financeira obrigatória'),
        agency: Yup.string().required('Agência obrigatória'),
        account: Yup.string().required('Conta obrigatória'),
        accountType: Yup.string().required('Tipo de conta obrigatório'),
        amount: Yup.string().required('Valor obrigatório'),
        date: Yup.string().required('Data obrigatória'),
      })}
      onSubmit={async (values, { resetForm, setSubmitting }) => {
        onSubmitting(true);

        try {
          await bankingService.createTransaction(EPixOperationType.BANK_DATA, {
            taxNumber: values.document.replace(/\D/g, ''),
            bankCode: values.bankIspb,
            bankBranch: values.agency,
            bankAccount: values.account,
            accountType: ACCOUNT_TYPE_MAP[values.accountType as EAccountTypePortuguese],
            value: parseCurrency(values.amount),
            transferDate: values.date,
            description: values.description || undefined,
          });

          resetForm();
          onSuccess();

          enqueueSnackbar('Transação criada com dados bancários com sucesso.', { variant: 'success' });
        } catch (error) {
          enqueueSnackbar('Erro ao criar transação com dados bancários. Tente novamente.', { variant: 'error' });
        } finally {
          onSubmitting(false);
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, handleChange, setFieldValue, handleSubmit, isValid, isSubmitting }) => (
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            variant="standard"
            label="Documento do destinatário *"
            placeholder="Digite aqui o CPF ou CNPJ do destinatário"
            value={values.document}
            onChange={(e) => setFieldValue('document', maskCpfCnpj(e.target.value))}
            error={Boolean(touched.document && errors.document)}
            helperText={touched.document && errors.document}
            sx={{ mb: 3 }}
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: '16px', fontWeight: 500 },
            }}
            InputProps={{ sx: { fontSize: '14px' } }}
          />

          <BrazilianBankSearch
            value={values.bankIspb}
            onChange={(ispb) => setFieldValue('bankIspb', ispb)}
            error={Boolean(touched.bankIspb && errors.bankIspb)}
            helperText={touched.bankIspb && errors.bankIspb}
          />

          <Box display="flex" gap={2} mb={3}>
            <TextField
              fullWidth
              variant="standard"
              label="Agência *"
              placeholder="0000"
              value={values.agency}
              onChange={handleChange('agency')}
              error={Boolean(touched.agency && errors.agency)}
              helperText={touched.agency && errors.agency}
              InputLabelProps={{
                shrink: true,
                sx: { fontSize: '16px', fontWeight: 500 },
              }}
              InputProps={{ sx: { fontSize: '14px' } }}
            />

            <TextField
              fullWidth
              variant="standard"
              label="Conta *"
              placeholder="000000"
              value={values.account}
              onChange={handleChange('account')}
              error={Boolean(touched.account && errors.account)}
              helperText={touched.account && errors.account}
              InputLabelProps={{
                shrink: true,
                sx: { fontSize: '16px', fontWeight: 500 },
              }}
              InputProps={{ sx: { fontSize: '14px' } }}
            />
          </Box>

          <TextField
            select
            fullWidth
            variant="standard"
            label="Tipo de conta *"
            value={values.accountType}
            onChange={handleChange('accountType')}
            error={Boolean(touched.accountType && errors.accountType)}
            helperText={touched.accountType && errors.accountType}
            sx={{ mb: 3 }}
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: '16px', fontWeight: 500 },
            }}
            InputProps={{ sx: { fontSize: '14px' } }}
          >
            <MenuItem value={EAccountTypePortuguese.CORRENTE}>Corrente</MenuItem>
            <MenuItem value={EAccountTypePortuguese.POUPANCA}>Poupança</MenuItem>
            <MenuItem value={EAccountTypePortuguese.PAGAMENTO}>Pagamento</MenuItem>
          </TextField>

          <TextField
            fullWidth
            variant="standard"
            label="Digite quanto quer pagar (R$) *"
            placeholder="R$ 20.000,00"
            value={values.amount}
            onChange={(e) => setFieldValue('amount', formatCurrency(e.target.value))}
            error={Boolean(touched.amount && errors.amount)}
            helperText={touched.amount && errors.amount}
            sx={{ mb: 3 }}
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: '16px', fontWeight: 500 },
            }}
            InputProps={{ sx: { fontSize: '14px' } }}
          />

          <TextField
            fullWidth
            variant="standard"
            label="Data de transferência *"
            name="date"
            type="date"
            value={values.date}
            onChange={handleChange}
            error={Boolean(touched.date && errors.date)}
            helperText={touched.date && errors.date}
            sx={{ mb: 3 }}
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: '16px', fontWeight: 500 },
            }}
            inputProps={{ min: new Date().toLocaleDateString('en-CA'), sx: { fontSize: '14px' } }}
          />

          <TextField
            fullWidth
            variant="standard"
            label="Descrição (Opcional)"
            placeholder="Pagamento de fornecedor"
            value={values.description}
            onChange={handleChange('description')}
            sx={{ mb: 4 }}
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: '16px', fontWeight: 500 },
            }}
            InputProps={{ sx: { fontSize: '14px' } }}
          />

          <Box display="flex" gap={2}>
            <Button fullWidth variant="outlined" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button fullWidth variant="contained" type="submit" disabled={isSubmitting || !isValid}>
              {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Confirmar'}
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  );
};

export default PixByBankData;
